import { API_MODE, delay } from "@/lib/api/helpers";
import { ApiError, getIdentity, mockRefusal, request } from "@/lib/api/http";
import { paths } from "@/lib/api/wire";
import { currentUser as mockCurrentUser } from "@/lib/mocks/db";
import { mockPermissions } from "@/lib/mocks/permissions";

/**
 * 2.790 — what a subject may do in the caller's organisation, and why: ONE read
 * of `GET /v1/people/{user_id}/permissions` (S-709, ADR-106).
 *
 * This replaces 2.666's union, which read every role, every role's holders and
 * each held role's grants, and assembled in the browser an answer that left out
 * organisation-administrator authority and the member baseline and said itself
 * that it was "not a decision the platform made". The answer here IS the
 * decision point's evaluation for the subject; its `sources` are what was
 * GRANTED. The two are kept apart in the types below as the contract keeps them.
 *
 * PARSED AT THE BOUNDARY (invariant 9). The answer is never partial on the
 * platform's side (a fault is a 503), so it is never made partial here either:
 * - a SHAPE the contract does not publish (a missing array, a non-string id, an
 *   answer about a different subject) fails the whole read — nothing is shown,
 *   because a permissions screen showing part of an answer under-reports;
 * - a NAME this build does not know (a relation, a source kind, a granted
 *   relation, a scope, a workspace capability) is KEPT, raw, and marked
 *   unrecognised. It is never dropped and never mapped to a neighbour: dropping
 *   a permission the platform reports is the one direction this screen must
 *   never be wrong in, and naming it as something else is worse.
 */

/** SDD Appendix A, *Effective permission vocabulary*, per object type. */
const ORGANISATION_RELATIONS = ["admin", "member"] as const;
const WORKSPACE_RELATIONS = ["admin"] as const;
const PROJECT_RELATIONS = [
  "admin", "viewer", "can_configure_targets", "can_launch_scan", "can_manage_issues", "can_request_reports",
] as const;
/** `EffectiveWorkspaceGrant.capability`, and Appendix A's *Role grant scope*. */
const WORKSPACE_GRANT_CAPABILITIES = ["can_configure_targets", "can_launch_scan", "can_manage_issues", "can_request_reports"] as const;
const GRANT_SCOPES = ["project", "workspace"] as const;

/** A name as served, and whether this build knows it for the object it was served on. */
export interface Named {
  name: string;
  known: boolean;
}

/**
 * Why a relation is held (SDD Appendix A, *Effective permission source*).
 * `unrecognised` keeps a kind this build does not know, as sent.
 */
export type PermissionSource =
  | { kind: "organisation_admin" }
  | { kind: "organisation_member" }
  | { kind: "role"; roleId?: string; granted?: Named; scope?: Named; workspaceId?: string }
  | { kind: "unrecognised"; raw: string };

export interface EffectiveRelation extends Named {
  /** `[]` means the platform permits it and cannot name the grant. */
  sources: PermissionSource[];
}

export interface Effective {
  userId: string;
  self: boolean;
  /** When the evaluation BEGAN. The answer is not atomic. */
  evaluatedAt: string;
  organisation: Named[];
  roles: { id: string; name: string }[];
  workspaces: {
    workspaceId: string;
    relations: EffectiveRelation[];
    /** Grants AS GRANTS: each covers every project there, including projects created later. */
    grants: { roleId: string; capability: Named }[];
  }[];
  projects: { projectId: string; workspaceId: string; relations: EffectiveRelation[] }[];
}

/** The read, one call. Throws ApiError for every refusal; see `isNotMember`. */
export async function readEffective(userId: string): Promise<Effective> {
  if (API_MODE === "http") {
    return parseEffective(await request<unknown>(paths.personPermissions(userId)), userId);
  }
  await delay(120);
  const raw = mockPermissions(userId);
  if (!raw) throw mockRefusal("NOT_FOUND", "Not a member of this organisation in the authorization graph.");
  return parseEffective(raw, userId);
}

/**
 * The caller's own subject id, as the token names it — what the self read
 * passes. Undefined in http mode when no identity has been pushed in.
 */
export function ownSubject(): string | undefined {
  const sub = getIdentity()?.sub;
  if (API_MODE === "http") return sub;
  return sub ?? mockCurrentUser.id;
}

/**
 * The contract's 404: absent, another organisation's, joined but not yet
 * signed in, or a revoked or expired key — ONE answer for all of them. Branched
 * on the machine code, not the status, so an intermediary's 404 page (UNKNOWN)
 * is a failed read rather than a statement about membership.
 */
export function isNotMember(error: unknown): boolean {
  return error instanceof ApiError && error.code === "NOT_FOUND";
}

/* ─────────────── parsing ─────────────── */

/** A 200 whose body is not the published shape: a failed read, said as one by ListError. */
class Malformed extends ApiError {
  constructor(what: string) {
    super(200, "UNKNOWN", `The platform's answer was not in the published shape (${what}), so none of it is shown.`);
  }
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

function str(v: unknown, what: string): string {
  if (typeof v !== "string") throw new Malformed(`${what} is not a string`);
  return v;
}

function optStr(v: unknown, what: string): string | undefined {
  return v === undefined ? undefined : str(v, what);
}

function arr(v: unknown, what: string): unknown[] {
  // Every array is ALWAYS present and never null in the contract.
  if (!Array.isArray(v)) throw new Malformed(`${what} is not a list`);
  return v;
}

function named(v: unknown, vocabulary: readonly string[], what: string): Named {
  const name = str(v, what);
  return { name, known: vocabulary.includes(name) };
}

function source(v: unknown, what: string): PermissionSource {
  if (!isObject(v)) throw new Malformed(`${what} is not an object`);
  const kind = str(v.kind, `${what}.kind`);
  if (kind === "organisation_admin" || kind === "organisation_member") return { kind };
  if (kind !== "role") return { kind: "unrecognised", raw: kind };
  return {
    kind: "role",
    roleId: optStr(v.role_id, `${what}.role_id`),
    granted: v.granted === undefined ? undefined : named(v.granted, PROJECT_RELATIONS, `${what}.granted`),
    scope: v.scope === undefined ? undefined : named(v.scope, GRANT_SCOPES, `${what}.scope`),
    workspaceId: optStr(v.workspace_id, `${what}.workspace_id`),
  };
}

function relations(v: unknown, vocabulary: readonly string[], what: string): EffectiveRelation[] {
  return arr(v, what).map((r, i) => {
    if (!isObject(r)) throw new Malformed(`${what}[${i}] is not an object`);
    return {
      ...named(r.relation, vocabulary, `${what}[${i}].relation`),
      sources: arr(r.sources, `${what}[${i}].sources`).map((s, j) => source(s, `${what}[${i}].sources[${j}]`)),
    };
  });
}

/** Strict: see the header. `userId` is the subject asked about; an answer about anyone else is refused. */
export function parseEffective(raw: unknown, userId: string): Effective {
  if (!isObject(raw)) throw new Malformed("not an object");
  const answered = str(raw.user_id, "user_id");
  if (answered !== userId) throw new Malformed("it describes a different subject");
  if (typeof raw.self !== "boolean") throw new Malformed("self is not a boolean");
  if (!isObject(raw.organisation)) throw new Malformed("organisation is not an object");
  return {
    userId: answered,
    self: raw.self,
    evaluatedAt: str(raw.evaluated_at, "evaluated_at"),
    organisation: arr(raw.organisation.relations, "organisation.relations")
      .map((r, i) => named(r, ORGANISATION_RELATIONS, `organisation.relations[${i}]`)),
    roles: arr(raw.roles, "roles").map((r, i) => {
      if (!isObject(r)) throw new Malformed(`roles[${i}] is not an object`);
      return { id: str(r.role_id, `roles[${i}].role_id`), name: str(r.name, `roles[${i}].name`) };
    }),
    workspaces: arr(raw.workspaces, "workspaces").map((w, i) => {
      if (!isObject(w)) throw new Malformed(`workspaces[${i}] is not an object`);
      return {
        workspaceId: str(w.workspace_id, `workspaces[${i}].workspace_id`),
        relations: relations(w.relations, WORKSPACE_RELATIONS, `workspaces[${i}].relations`),
        grants: arr(w.grants, `workspaces[${i}].grants`).map((g, j) => {
          if (!isObject(g)) throw new Malformed(`workspaces[${i}].grants[${j}] is not an object`);
          return {
            roleId: str(g.role_id, `workspaces[${i}].grants[${j}].role_id`),
            capability: named(g.capability, WORKSPACE_GRANT_CAPABILITIES, `workspaces[${i}].grants[${j}].capability`),
          };
        }),
      };
    }),
    projects: arr(raw.projects, "projects").map((p, i) => {
      if (!isObject(p)) throw new Malformed(`projects[${i}] is not an object`);
      return {
        projectId: str(p.project_id, `projects[${i}].project_id`),
        workspaceId: str(p.workspace_id, `projects[${i}].workspace_id`),
        relations: relations(p.relations, PROJECT_RELATIONS, `projects[${i}].relations`),
      };
    }),
  };
}
